import { HexColorPicker, HexColorInput } from 'react-colorful';
import { Popover } from '@printtat/ui';

interface ElementColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  label?: string;
}

/**
 * Detect neon/fluorescent colors that DTF printers can't reproduce.
 * Heuristic: one RGB channel near max, another near zero, large delta.
 */
const isUnprintableColor = (hex: string): boolean => {
  if (!hex || hex.length < 7) return false;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  return max > 240 && min < 30 && (max - min) > 200;
};

export const ElementColorPicker = ({ color, onChange, label }: ElementColorPickerProps) => {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <div className="flex items-center gap-1.5 bg-gray-100 rounded-md px-2 py-1 cursor-pointer hover:ring-1 hover:ring-blue-400 transition-shadow">
          {label && (
            <span className="text-[10px] text-gray-400 flex-shrink-0 w-3 text-center select-none">
              {label}
            </span>
          )}
          <button
            className="w-4 h-4 rounded border border-gray-300 flex-shrink-0"
            style={{ backgroundColor: color }}
            title={color}
          />
          <span className="text-xs text-gray-800 font-mono flex-1 min-w-0">{color.toUpperCase()}</span>
        </div>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 z-50"
          sideOffset={8}
          align="start"
        >
          <HexColorPicker color={color} onChange={onChange} />
          <div className="mt-2 flex items-center gap-1.5">
            <span className="text-[10px] text-gray-400">#</span>
            <HexColorInput
              color={color}
              onChange={onChange}
              className="flex-1 bg-gray-100 rounded px-2 py-1 text-xs text-gray-800 font-mono focus:outline-none focus:ring-1 focus:ring-blue-400"
              prefixed={false}
            />
          </div>
          {isUnprintableColor(color) && (
            <p className="mt-1.5 text-[10px] text-amber-600">
              This color may not print accurately with DTF
            </p>
          )}
          <Popover.Arrow className="fill-white" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
